import { useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Divider, IconButton, MenuItem,
  Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  internalTransfersApi, stockApi, warehousesApi,
  type ItemDto, type StockAvailabilityDto,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { ItemPicker } from "@/components/Pickers";

interface DraftLine {
  key: string; item: ItemDto | null;
  batches: StockAvailabilityDto[]; batchId: string; qty: number;
}
const blank = (): DraftLine => ({
  key: crypto.randomUUID(), item: null, batches: [], batchId: "", qty: 0,
});

/**
 * UC-M1-INV-07 — Same-branch warehouse transfer. Dispatch on the detail page
 * writes a TRANSFER_OUT + IN_TRANSIT_OUT pair at source; Receive writes the
 * matching IN_TRANSIT_IN + TRANSFER_IN at destination per received qty.
 */
export function CreateInternalTransferPage() {
  const navigate = useNavigate();
  const [sourceWarehouseId, setSourceWarehouseId] = useState("");
  const [destWarehouseId, setDestWarehouseId] = useState("");
  const [documentDate, setDocumentDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([blank()]);

  const warehouses = useQuery({
    queryKey: ["warehouses", "all"],
    queryFn: () => warehousesApi.list({ pageSize: 200 }),
  });

  const update = (key: string, patch: Partial<DraftLine>) =>
    setLines(ls => ls.map(l => l.key === key ? { ...l, ...patch } : l));
  const remove = (key: string) =>
    setLines(ls => ls.length === 1 ? ls : ls.filter(l => l.key !== key));

  const onItemChange = async (key: string, item: ItemDto | null) => {
    if (!item || !sourceWarehouseId) {
      update(key, { item, batches: [], batchId: "" });
      return;
    }
    const batches = await stockApi.availability({ itemId: item.id, warehouseId: sourceWarehouseId });
    update(key, { item, batches, batchId: batches[0]?.batchId ?? "" });
  };

  const validate = (): string | null => {
    if (!sourceWarehouseId) return "Pick a source warehouse.";
    if (!destWarehouseId) return "Pick a destination warehouse.";
    if (sourceWarehouseId === destWarehouseId) return "Source and destination must differ.";
    for (const [i, l] of lines.entries()) {
      if (!l.item) return `Line ${i + 1}: pick an item.`;
      if (!l.batchId) return `Line ${i + 1}: pick a batch.`;
      if (l.qty <= 0) return `Line ${i + 1}: qty must be > 0.`;
      const batch = l.batches.find(b => b.batchId === l.batchId);
      if (batch && l.qty > batch.availableQty)
        return `Line ${i + 1}: only ${batch.availableQty} available at source.`;
    }
    return null;
  };

  const mutation = useMutation({
    meta: { successMessage: "Internal transfer created" },
    mutationFn: () => internalTransfersApi.create({
      sourceWarehouseId, destWarehouseId, documentDate,
      reason: reason || undefined, notes: notes || undefined,
      lines: lines.map(l => ({ itemId: l.item!.id, batchId: l.batchId, qty: l.qty })),
    }),
    onSuccess: (r) => navigate(`/inventory/internal-transfers/${r.id}`),
  });

  const validationError = validate();

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>New internal transfer</Typography>
      <Paper sx={{ p: 3 }}>
        {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}

        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
          <TextField select label="Source warehouse *" size="small" sx={{ minWidth: 260 }}
            value={sourceWarehouseId}
            onChange={(e) => { setSourceWarehouseId(e.target.value); setLines([blank()]); }}>
            <MenuItem value="">— pick source —</MenuItem>
            {warehouses.data?.items.map(w => (
              <MenuItem key={w.id} value={w.id}>{w.code} — {w.name}</MenuItem>
            ))}
          </TextField>
          <TextField select label="Destination warehouse *" size="small" sx={{ minWidth: 260 }}
            value={destWarehouseId} onChange={(e) => setDestWarehouseId(e.target.value)}>
            <MenuItem value="">— pick destination —</MenuItem>
            {warehouses.data?.items.filter(w => w.id !== sourceWarehouseId).map(w => (
              <MenuItem key={w.id} value={w.id}>{w.code} — {w.name}</MenuItem>
            ))}
          </TextField>
          <TextField type="date" label="Date *" size="small" InputLabelProps={{ shrink: true }}
            value={documentDate} onChange={(e) => setDocumentDate(e.target.value)} />
        </Stack>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
          <TextField label="Reason" size="small" sx={{ flex: 1 }}
            value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. main → OPD top-up" />
          <TextField label="Notes" size="small" sx={{ flex: 1 }}
            value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Stack>

        <Divider sx={{ my: 2 }} />
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Lines</Typography>
          <Button startIcon={<AddIcon />} size="small"
            onClick={() => setLines(ls => [...ls, blank()])} disabled={!sourceWarehouseId}>Add line</Button>
        </Stack>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell sx={{ minWidth: 280 }}>Item *</TableCell>
            <TableCell sx={{ minWidth: 240 }}>Batch *</TableCell>
            <TableCell align="right" sx={{ width: 110 }}>Qty *</TableCell>
            <TableCell sx={{ width: 50 }} />
          </TableRow></TableHead>
          <TableBody>
            {lines.map((l, idx) => {
              const batch = l.batches.find(b => b.batchId === l.batchId);
              return (
                <TableRow key={l.key}>
                  <TableCell>
                    <ItemPicker value={l.item} onChange={(v) => onItemChange(l.key, v)}
                      label={`Line ${idx + 1}`} required error={!l.item} />
                  </TableCell>
                  <TableCell>
                    <TextField select size="small" fullWidth value={l.batchId}
                      onChange={(e) => update(l.key, { batchId: e.target.value })}
                      disabled={l.batches.length === 0}>
                      <MenuItem value="">— pick batch —</MenuItem>
                      {l.batches.map(b => (
                        <MenuItem key={b.batchId} value={b.batchId}>
                          {b.batchNo}{b.expiryDate ? ` · exp ${new Date(b.expiryDate).toLocaleDateString()}` : ""} · avail {b.availableQty}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell align="right">
                    <TextField type="number" size="small" sx={{ width: 100 }}
                      value={l.qty} onChange={(e) => update(l.key, { qty: Number(e.target.value) || 0 })}
                      inputProps={{ min: 0, step: "0.001", style: { textAlign: "right" } }}
                      helperText={batch ? `avail ${batch.availableQty}` : ""} />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => remove(l.key)} disabled={lines.length === 1}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 3 }}>
          <Button onClick={() => navigate("/inventory/internal-transfers")}>Cancel</Button>
          <Button variant="contained" disabled={!!validationError || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={() => mutation.mutate()}>Save draft transfer</Button>
        </Stack>
        {validationError && <Alert severity="info" sx={{ mt: 2 }}>{validationError}</Alert>}
      </Paper>
    </Box>
  );
}
